import { ENDPOINTS } from "../utils/appConfig";
import { fetchWithAuthRetry, getAuthHeaders } from "../utils/helpers";

export async function fetchDeviceList() {
  const response = await fetchWithAuthRetry(ENDPOINTS.DEVICE_LIST, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return await response.json();
}

export async function fetchDeviceDetail(deviceId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.DEVICE_DETAIL(deviceId), {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return await response.json();
}

export async function fetchDeviceConfiguration(deviceId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.DEVICE_CONFIG(deviceId), {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return await response.json();
}

export async function submitDeviceOrder(name, type) {
  try {
    const response = await fetch(ENDPOINTS.DEVICE_ORDER, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, type }),
    });

    if (response.status === 409) {
      return { success: false, error: `Device with name '${name}' already exists.` };
    }
    if (!response.ok) throw new Error('Download failed');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/\s+/g, '_')}_device.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { success: true };
  } catch (error) {
    console.error('Failed to submit device order:', error);
    return { success: false, error: 'Failed to submit device order.' };
  }
}

export async function fetchLiveFeeds() {
  const response = await fetchWithAuthRetry(ENDPOINTS.LIVE_FEEDS, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return await response.json();
}

export async function fetchCameraDetails(cameraId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.CAMERA_DETAILS(cameraId), {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return await response.json();
}

export async function fetchDroneRunwayAlerts() {
  const response = await fetchWithAuthRetry(ENDPOINTS.DRONE_RUNWAY_ALERTS, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return Array.isArray(data) ? data : data?.results || [];
}

export async function fetchForeignObjectAlerts(droneId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.FOREIGN_OBJECT_ALERTS(droneId), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Foreign object alerts endpoint returned ${response.status}`);
  const data = await response.json();
  return Array.isArray(data) ? data : data?.results || [];
}

export async function triggerForeignObjectDetection(droneId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.FOREIGN_OBJECT_DETECTION, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ drone_id: droneId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Foreign object detection endpoint returned ${response.status}`);
  }
  return await response.json();
}

export async function fetchActiveDrones() {
  const response = await fetchWithAuthRetry(ENDPOINTS.DRONES_ACTIVE, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return Array.isArray(data) ? data : data?.results || [];
}

export async function fetchDroneWaypoints(droneId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.DRONE_WAYPOINTS(droneId), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return Array.isArray(data) ? data : data?.results || [];
}

// Returns the drone's existing Path id, creating one if none exists.
export async function ensureDronePath(droneId, name) {
  const listResp = await fetchWithAuthRetry(ENDPOINTS.DRONE_PATHS(droneId), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!listResp.ok) throw new Error(`Error ${listResp.status}: ${listResp.statusText}`);
  const listData = await listResp.json();
  const paths = Array.isArray(listData) ? listData : listData?.results || [];
  if (paths.length) return paths[0].id;

  const createResp = await fetchWithAuthRetry(ENDPOINTS.PATHS, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ path: name || `Drone ${droneId} path`, drone: droneId }),
  });
  if (!createResp.ok) throw new Error(`Error ${createResp.status}: ${createResp.statusText}`);
  const created = await createResp.json();
  return created.id;
}

// Returns the camera's existing Path id, creating one if none exists.
export async function ensureCameraPath(cameraId, name) {
  const listResp = await fetchWithAuthRetry(ENDPOINTS.CAMERA_PATHS(cameraId), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!listResp.ok) throw new Error(`Error ${listResp.status}: ${listResp.statusText}`);
  const listData = await listResp.json();
  const paths = Array.isArray(listData) ? listData : listData?.results || [];
  if (paths.length) return paths[0].id;

  const createResp = await fetchWithAuthRetry(ENDPOINTS.PATHS, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ path: name || `Camera ${cameraId} path`, camera: cameraId }),
  });
  if (!createResp.ok) throw new Error(`Error ${createResp.status}: ${createResp.statusText}`);
  const created = await createResp.json();
  return created.id;
}

// A camera's dispatch path waypoints, order-sorted by the API.
export async function fetchCameraWaypoints(cameraId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.CAMERA_WAYPOINTS(cameraId), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return Array.isArray(data) ? data : data?.results || [];
}

export async function saveWaypoint(waypoint) {
  const response = await fetchWithAuthRetry(ENDPOINTS.WAYPOINTS, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(waypoint),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return await response.json();
}

export async function updateWaypoint(id, waypoint) {
  const response = await fetchWithAuthRetry(ENDPOINTS.WAYPOINT_DETAIL(id), {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(waypoint),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return await response.json();
}

export async function deleteWaypoint(id) {
  const response = await fetchWithAuthRetry(ENDPOINTS.WAYPOINT_DETAIL(id), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return true;
}
